import { UI, HStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useRecoilValue } from 'recoil';

import { FilterModalTypes } from '../../../../../../components/FilterModal';
import { SectionTypes } from '../../../../../../components/FilterModal/filters/Section';
import { assetsFiltersAtom } from '../../../../../../store/product/filters';
import { useGlobalAssetContext } from '../../contexts';
import { useGlobalAssetController } from '../../hooks';

export interface AssetCategoriesNormalized {
  title: string;
  value: string;
}

export const AssetCategories = () => {
  const { appliedFilters } = useRecoilValue(assetsFiltersAtom);

  const { assetCategories, loadingCategories, hasSomeLoading, selectedTab } =
    useGlobalAssetContext();
  const { handleClearSearchTerm, applyCategoriesFilter } = useGlobalAssetController();

  const getCorrectFilter = (): FilterModalTypes.SelectedFiltersProps | undefined => {
    return appliedFilters.find(({ fieldName }) => fieldName === selectedTab.categoriesField);
  };

  const getSelectedCategories = (
    correctFilter: FilterModalTypes.SelectedFiltersProps
  ): AssetCategoriesNormalized[] => {
    return assetCategories.filter(({ value }) =>
      (correctFilter.filter as SectionTypes.SelectionProps).includes(value)
    );
  };

  const getCorrectSelectedIndexes = (selectedCategories: AssetCategoriesNormalized[]) => {
    return selectedCategories.map(({ value }) =>
      assetCategories.findIndex((category) => category.value === value)
    );
  };

  const getActiveTabs = (): number[] => {
    const correctFilter = getCorrectFilter();

    if (!correctFilter) {
      return [0];
    }

    const selectedCategories = getSelectedCategories(correctFilter);

    return getCorrectSelectedIndexes(selectedCategories);
  };

  const getCurrentSelectedCategories = (): AssetCategoriesNormalized[] => {
    const correctFilter = getCorrectFilter();

    if (!correctFilter) {
      return [];
    }

    return getSelectedCategories(correctFilter);
  };

  const handleTabPress = (index: number) => {
    handleClearSearchTerm();

    if (index === 0) {
      applyCategoriesFilter(selectedTab.categoriesField, []);
      return;
    }

    const currentSelectedCategories = getCurrentSelectedCategories();
    const currentSelectedValues = currentSelectedCategories.map(({ value }) => value);
    let newSelection: string[] = [];

    if (currentSelectedCategories.includes(assetCategories[index])) {
      newSelection = currentSelectedValues.filter(
        (value) => value !== assetCategories[index].value
      );
    } else {
      newSelection = [...currentSelectedValues, assetCategories[index].value];
    }

    applyCategoriesFilter(selectedTab.categoriesField, newSelection);
  };

  if (!selectedTab.hasCategories) {
    return <></>;
  }

  return (
    <HStack space={Spacing['inline.xxxs']} alignItems="center" maxW="75%">
      <UI.Text variant="semibold">{selectedTab.categoriesLabel}:</UI.Text>
      <UI.Tabs
        testId="category-tabs"
        isDisabled={hasSomeLoading}
        isLoading={loadingCategories}
        activeTab={getActiveTabs()}
        variant="secondary"
        onTabPress={handleTabPress}
        tabs={assetCategories}
      />
    </HStack>
  );
};
